import { useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { backendService } from "../../../services/backend.service";

export const RESEND_WELCOME_PREFLIGHT_QUERY_KEY = "team-resend-welcome-preflight";

/**
 * When each nurse in this department last had a welcome sent.
 *
 * Shared by the resend dialog (which warns about a same-day re-send) and the roster's Last Login
 * column (which reads "Invite sent {date}" instead of "Never" for someone invited today). Same query
 * key as the dialog's own fetch, so the two share one response rather than racing for it.
 */
export const useWelcomeSendHistory = ({ enabled = true }: { enabled?: boolean } = {}) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = chosenDepartment.department_id;

	const query = useQuery({
		queryKey: [RESEND_WELCOME_PREFLIGHT_QUERY_KEY, departmentId],
		queryFn: async () => {
			const { data } = await backendService.getResendWelcomePreflight(departmentId);
			return data;
		},
		enabled: enabled && !!departmentId,
	});

	/** Nurse id → the day their most recent welcome went out, for nurses invited TODAY. */
	const invitedTodayByNurseId = useMemo(() => {
		const map = new Map<number, DateTime>();
		for (const entry of query.data?.nurses ?? []) {
			// The backend decides "today" in the department's time zone; the browser's may be a day off.
			if (entry.sentToday && entry.lastWelcomeSentAt) map.set(entry.nurseId, DateTime.fromISO(entry.lastWelcomeSentAt));
		}
		return map;
	}, [query.data]);

	return { query, invitedTodayByNurseId, invitesSentToday: query.data?.invitesSentToday ?? 0 };
};
